import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { ChevronLeft, X, Calendar, Clock, Building2, Wrench, Factory, Timer, ClipboardCheck } from "lucide-react";

import { getMethodApiCall } from "src/services/api";
import GenericDataLayout from "../../../components/custom/GenericDataLayout";
import GenericTable from "../../../components/custom/GenericTable";

// ── Attendance Detail Modal ──────────────────────────────────────────────────

// A single label/value pair. Stacked (label above value) rather than side-by-side
// so long values (addresses, long ITI names, etc.) always wrap cleanly without
// fighting a fixed label column width.
const InfoRow = ({ label, value, icon: Icon, className = "" }) => (
  <div className={`flex flex-col gap-1 min-w-0 ${className}`}>
    <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {Icon && <Icon size={12} className="shrink-0" />}
      {label}
    </span>
    <span className="text-sm font-medium text-gray-800 break-words">
      {value || <span className="text-gray-400 font-normal">—</span>}
    </span>
  </div>
);

const ResponseAnswer = ({ response }) => {
  const { assessment_master: q, assessment_options: opt, text_answer, rating_value } = response;
  if (q.type === "rating") {
    return (
      <div className="flex gap-1 mt-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-semibold border ${
              n <= rating_value
                ? "bg-primary border-primary text-white"
                : "bg-gray-100 border-gray-200 text-gray-400"
            }`}
          >
            {n}
          </span>
        ))}
      </div>
    );
  }

  if (q.type === "short_text" || q.type === "long_text") {
    return <p className="mt-1 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 break-words">{text_answer}</p>;
  }

  // single_choice / multiple_choice
  if (opt) {
    return (
      <span className="mt-1 inline-block bg-primary/10 text-primary text-xs font-medium px-3 py-1 rounded-full break-words">
        {opt.option}
        {opt.hindi_translation && (
          <span className="ml-1 text-primary/70">({opt.hindi_translation})</span>
        )}
      </span>
    );
  }

  // "other" text row for choice questions
  if (text_answer) {
    return <p className="mt-1 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 break-words">{text_answer}</p>;
  }

  return null;
};

const AttendanceDetailModal = ({ attendanceId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!attendanceId) return;
    setLoading(true);
    getMethodApiCall(`/v1/api/trainee/attendances/${attendanceId}`)
      .then((res) => { if (res?.data?.success) setDetail(res.data.data); })
      .finally(() => setLoading(false));
  }, [attendanceId]);

  // Group responses by section

    const assessment_sections = (() => {
    if (!detail?.assessment_responses?.length) return [];
    const map = new Map();
    detail.assessment_responses.forEach((r) => {
      const sec = r.assessment_master?.assessment_sections;
      const key = sec?.section || "General";
      if (!map.has(key)) map.set(key, { section: key, hindi_section: sec?.hindi_section, questions: new Map() });
      const qKey = String(r.assessment_master?.title);
      const bucket = map.get(key);
      if (!bucket.questions.has(qKey)) {
        bucket.questions.set(qKey, { title: r.assessment_master?.title, hindi_title: r.assessment_master?.hindi_title, type: r.assessment_master?.type, responses: [] });
      }
      bucket.questions.get(qKey).responses.push(r);
    });
    return [...map.values()].map((s) => ({ ...s, questions: [...s.questions.values()] }));
  })();


  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-primary">Attendance Details</h2>
            {detail && (
              <p className="text-xs text-gray-500 mt-1">{moment(detail.date).format("dddd, DD MMM YYYY")}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1.5 transition-colors -mt-1 -mr-1"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          ) : !detail ? (
            <p className="text-center text-sm text-gray-500 py-10">Could not load details.</p>
          ) : (
            <>
              {/* ── Attendance Info ── */}
              <div className="bg-gray-50 rounded-xl p-4 sm:p-5">
                <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-4">Attendance Info</p>
                <div className="grid grid-cols-2 gap-x-3 sm:gap-x-6 gap-y-4">
                  <InfoRow label="Date" icon={Calendar} value={moment(detail.date).format("DD MMM YYYY")} />
                  <InfoRow label="Status" icon={ClipboardCheck} value={detail.status} />

                  <InfoRow label="Check-In" icon={Clock} value={detail.checkin_time ? moment(detail.checkin_time).format("h:mm A") : <span className="text-red-500">Pending</span>} />
                  <InfoRow label="Check-Out" icon={Clock} value={detail.checkout_time ? moment(detail.checkout_time).format("h:mm A") : <span className="text-red-500">Pending</span>} />

                  <InfoRow label="Hours Logged" icon={Timer} value={detail.hours_logged} />
                  <InfoRow
                    label="Daily Reflection"
                    value={
                      detail.checkout_questions_filled ? (
                        <span className="text-green-600 font-semibold">Submitted</span>
                      ) : (
                        <span className="text-red-500 font-semibold">Not Submitted</span>
                      )
                    }
                  />

                  <InfoRow label="ITI" icon={Building2} value={detail.iti_master?.iti_name} />
                  <InfoRow label="Trade" icon={Wrench} value={detail.trade_master?.trade_name} />

                  <InfoRow
                    label="Industry"
                    icon={Factory}
                    value={detail.industry_master?.industry_name}
                    className="col-span-2"
                  />
                </div>
              </div>

              {/* ── Daily Reflection Responses ── */}
              {assessment_sections.length > 0 && (
                <div className="space-y-4">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide">Daily Reflection Responses</p>
                  {assessment_sections.map((sec) => (
                    <div key={sec.section} className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                      {/* Section header */}
                      <div className="bg-primary/5 border-b border-primary/10 px-4 py-3">
                        <p className="text-sm font-semibold text-primary">{sec.section}</p>
                        {sec.hindi_section && (
                          <p className="text-xs text-primary/60 mt-0.5">{sec.hindi_section}</p>
                        )}
                      </div>
                      {/* Questions */}
                      <div className="divide-y divide-gray-50">
                        {sec.questions.map((q, qi) => (
                          <div key={qi} className="px-4 py-3.5">
                            <p className="text-[13px] font-semibold text-gray-800 break-words">
                              {qi + 1}. {q.title}
                            </p>
                            {q.hindi_title && (
                              <p className="text-xs text-gray-500 mt-0.5 ml-4 break-words">{q.hindi_title}</p>
                            )}
                            <div className="ml-4 mt-1.5 flex flex-wrap gap-1.5">
                              {q.responses.map((r, ri) => (
                                <ResponseAnswer key={ri} response={r} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {detail.checkout_questions_filled === false && (
                <p className="text-center text-xs text-gray-400 pb-2">No Daily Reflection responses recorded for this attendance.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Layout ──────────────────────────────────────────────────────────────

const AttendanceHistoryLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ data: [], meta: {} });
  const [loading, setLoading] = useState(false);
  const [selectedAttendanceId, setSelectedAttendanceId] = useState(null);

  const [startDate, setStartDate] = useState(moment().subtract(1, "week").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().add(1, "day").format("YYYY-MM-DD"));

  const getTraineeAttendance = async () => {
    setLoading(true);
    try {
      const storedData = localStorage.getItem("loginedUser");
      let traineeId;
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        traineeId = parsedData?.trainee?.id;
      }
      let url = `/v1/api/trainee/attendance/get?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`;
      if (traineeId) url += `&trainee_id=${traineeId}`;
      const res = await getMethodApiCall(url);
      if (res?.status === 200) setData(res?.data);
    } catch (error) {
      console.error("Error fetching attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTraineeAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, startDate, endDate]);

  const handleClearFilters = () => {
    setPage(1);
    setLimit(10);
    setStartDate(moment().subtract(1, "week").format("YYYY-MM-DD"));
    setEndDate(moment().add(1, "day").format("YYYY-MM-DD"));
  };

  const filtersConfig = [
    {
      type: "date",
      label: t("attendance.startDate"),
      value: startDate,
      onChange: (v) => { setStartDate(v); setPage(1); },
      max: endDate,
    },
    {
      type: "date",
      label: t("attendance.endDate"),
      value: endDate,
      onChange: (v) => { setEndDate(v); setPage(1); },
      min: startDate,
    },
  ];

  const paginationConfig = {
    page,
    limit,
    totalRecords: data?.meta?.totalRecords || 0,
    totalPages: data?.meta?.totalPages || 1,
    onPageChange: (newPage) => setPage(newPage),
    onLimitChange: (newLimit) => { setLimit(newLimit); setPage(1); },
  };

  const columns = [
    { header: t("trainer.table.date"), render: (row) => moment(row.date).format("DD/MM/YYYY") },
    { header: t("attendance.table.attendance"), render: (row) => row?.status },
    { header: t("attendance.table.iti"), render: (row) => row.iti_master?.iti_name || "—" },
    { header: t("attendance.table.trade"), render: (row) => row?.trade_master?.trade_name || "—" },
    {
      header: t("attendance.table.industry"),
      render: (row) => (
        <div className="max-w-[150px] truncate" title={row?.industry_master?.industry_name}>
          {row?.industry_master?.industry_name || "—"}
        </div>
      ),
    },
    {
      header: t("attendance.table.checkout_questions"),
      render: (row) =>
        row?.checkout_questions_filled ? (
          <span className="text-green-500 text-xs font-medium">Submitted</span>
        ) : (
          <span className="text-red-500 text-xs font-medium">Not Submitted</span>
        ),
    },
    {
      header: t("itiHome.checkIn"),
      render: (row) =>
        row?.checkin_time ? (
          moment(row.checkin_time).format("h:mm A")
        ) : (
          <span className="text-red-500 text-xs font-medium">Pending</span>
        ),
    },
    {
      header: t("itiHome.checkOut"),
      render: (row) =>
        row?.checkout_time ? (
          moment(row.checkout_time).format("h:mm A")
        ) : (
          <span className="text-red-500 text-xs font-medium">Pending</span>
        ),
    },
    { header: t("attendance.table.hours_logged"), render: (row) => row?.hours_logged || "—" },
  ];

  const actionsConfig = [
    {
      label: "Back",
      onClick: () => navigate(-1),
      className: "bg-white text-primary border border-primary hover:bg-gray-50",
      icon: ChevronLeft,
    },
  ];

  return (
    <>
      <GenericDataLayout
        title={t("attendance.title")}
        filters={filtersConfig}
        pagination={paginationConfig}
        isLoading={loading}
        actions={actionsConfig}
        onResetFilters={handleClearFilters}
      >
        <GenericTable
          columns={columns}
          data={data?.data || []}
          pagination={paginationConfig}
          onRowClick={(row) => setSelectedAttendanceId(row.id)}
        />
      </GenericDataLayout>

      {selectedAttendanceId && (
        <AttendanceDetailModal
          attendanceId={selectedAttendanceId}
          onClose={() => setSelectedAttendanceId(null)}
        />
      )}
    </>
  );
};

export default AttendanceHistoryLayout;